import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { FileText, Maximize2 } from 'lucide-react';
import * as React from 'react';
import { evaluateFormula } from '../../lib/formula';
import { SpecraChecklist } from '../specra/captains/checklist';
import { SpecraFlowDiagram } from '../specra/captains/flow-diagram';
import { SpecraGantt } from '../specra/captains/gantt';
import { SpecraMetricStrip } from '../specra/captains/metric-strip';
import { SpecraOverlayDialog } from '../specra/captains/overlay-dialog';
import { SpecraSankey } from '../specra/captains/sankey';
import { SpecraScenario } from '../specra/captains/scenario';
import { SpecraStackedBarList } from '../specra/captains/stacked-bar-list';
import { SpecraTileMap } from '../specra/captains/tile-map';
import { SpecraTimeline } from '../specra/captains/timeline';
import { SpecraTreemap } from '../specra/captains/treemap';
import { SpecraTrendChart } from '../specra/captains/trend-chart';
import { SpecraButton } from '../specra/ensigns/button';
import { SpecraIconButton } from '../specra/ensigns/icon-button';
import { SpecraSpinner } from '../specra/ensigns/spinner';
import { SpecraStatusDot } from '../specra/ensigns/status-dot';
import { SpecraText } from '../specra/ensigns/typography';
import { SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle } from '../specra/lieutenants/card';
import { SpecraCarousel } from '../specra/lieutenants/carousel';
import { SpecraInlineBanner } from '../specra/lieutenants/inline-banner';
import { SpecraKeyValueRow } from '../specra/lieutenants/key-value-row';
import { SpecraRiskMatrix } from '../specra/lieutenants/risk-matrix';
import { SpecraStatusPill } from '../specra/lieutenants/status-pill';
import { SpecraTabBar, SpecraTabPanel } from '../specra/lieutenants/tab-bar';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { SpecraCountUp } from './count-up';
import { CustomFrame } from './custom-frame';
import { declaredInputs, isFormula, splitVisuals, visualCatalog } from './parse';
import { aggregate, cellNumber, parseCsv, resolveColumn } from './table-data';
const HostContext = /*#__PURE__*/ React.createContext({});
const ReplyInputsContext = /*#__PURE__*/ React.createContext(null);
function hasFormula(node) {
    return Object.values(node.attrs).some(isFormula) || node.children.some((c)=>typeof c !== 'string' && hasFormula(c));
}
/**
 * The node with every {=formula} replaced by its value at the current
 * inputs: a number where the tag takes one, else the value in the unit of
 * the tag or its nearest parent ($, %, count…). Renderers never see formulas.
 */ function resolveNode(node, values, unit = '') {
    const ownUnit = typeof node.attrs.unit === 'string' ? node.attrs.unit : unit;
    const specAttrs = visualCatalog.tags[node.tag]?.attrs ?? {};
    const attrs = {};
    for (const [name, v] of Object.entries(node.attrs)){
        if (!isFormula(v)) {
            attrs[name] = v;
            continue;
        }
        const n = evaluateFormula(v['='], values);
        attrs[name] = specAttrs[name]?.type === 'number' ? n : Number.isFinite(n) ? formatUnit(n, ownUnit) : '—';
    }
    return {
        ...node,
        attrs,
        children: node.children.map((c)=>typeof c === 'string' ? c : resolveNode(c, values, ownUnit))
    };
}
const str = (v)=>v === undefined || v === null ? '' : String(v);
const num = (v)=>typeof v === 'number' ? v : Number(v);
const tone = (v)=>typeof v === 'string' ? v : undefined;
const kids = (node, tag)=>node.children.filter((c)=>typeof c !== 'string' && c.tag === tag);
const text = (node)=>node.children.filter((c)=>typeof c === 'string').join('\n\n');
/** Formats a number in the chart's unit. */ export function formatUnit(value, unit) {
    const n = (d)=>value.toLocaleString('en-US', {
            maximumFractionDigits: d
        });
    const abs = Math.abs(value);
    // Big figures read at a glance: $1.8B, $260M, 4.2M; the table keeps them exact.
    const compact = (prefix)=>{
        const sign = value < 0 ? '-' : '';
        if (abs >= 1e9) return `${sign}${prefix}${(abs / 1e9).toFixed(abs >= 1e10 ? 1 : 2).replace(/\.?0+$/, '')}B`;
        if (abs >= 1e6) return `${sign}${prefix}${(abs / 1e6).toFixed(abs >= 1e8 ? 0 : 1).replace(/\.0$/, '')}M`;
        return null;
    };
    switch(unit){
        case '$':
            return compact('$') ?? `$${n(0)}`;
        case 'count':
            return compact('') ?? n(0);
        case '$K':
            return `$${n(1)}K`;
        case '$M':
            return `$${n(1)}M`;
        case '$B':
            return `$${n(2)}B`;
        case '%':
            return `${n(1)}%`;
        case 'hours':
            return `${n(0)} hrs`;
        case 'FTE':
            return `${n(1)} FTE`;
        default:
            return compact('') ?? n(1);
    }
}
function Heading({ title }) {
    if (!title) return null;
    return /*#__PURE__*/ _jsx("div", {
        className: "mb-2 text-[length:var(--fs-ui-caption)] font-medium text-foreground",
        children: title
    });
}
function Source({ source }) {
    if (!source) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-1.5 text-[length:var(--fs-ui-caption)] text-muted-foreground",
        children: [
            "Source: ",
            source
        ]
    });
}
function Stats({ node }) {
    const metrics = kids(node, 'Stat').map((s, i)=>{
        const source = str(s.attrs.source);
        const info = [
            str(s.attrs.info),
            source && `Source: ${source}`
        ].filter(Boolean).join(' · ');
        return {
            id: `stat-${i}`,
            label: str(s.attrs.label),
            value: /*#__PURE__*/ _jsx(SpecraCountUp, {
                value: str(s.attrs.value)
            }),
            note: str(s.attrs.note) || undefined,
            tone: tone(s.attrs.tone),
            info: info || undefined
        };
    });
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Heading, {
                title: str(node.attrs.title)
            }),
            /*#__PURE__*/ _jsx(SpecraMetricStrip, {
                metrics: metrics
            })
        ]
    });
}
const SERIES_TONES = [
    'series',
    'good',
    'warn',
    'pending',
    'bad'
];
/** The chart a CSV becomes: Bar / Point / Segment rows built from its aggregate. */ function chartFromTable(node, a) {
    const type = str(node.attrs.type);
    const attrs = {
        ...node.attrs
    };
    for (const k of [
        'file',
        'x',
        'y',
        'agg',
        'top',
        'series'
    ])delete attrs[k];
    const kidsOut = [];
    const at = (n)=>node.at + n + 1;
    if (type === 'stacked') {
        a.series.forEach((s, i)=>kidsOut.push({
                tag: 'Segment',
                attrs: {
                    key: s,
                    label: s
                },
                children: [],
                at: at(i),
                keys: []
            }));
        a.rows.forEach((r, i)=>kidsOut.push({
                tag: 'Bar',
                attrs: {
                    label: r.x,
                    values: r.values
                },
                children: [],
                at: at(1000 + i),
                keys: []
            }));
    } else {
        a.rows.forEach((r, i)=>kidsOut.push({
                tag: type === 'line' ? 'Point' : 'Bar',
                attrs: {
                    label: r.x,
                    value: r.total
                },
                children: [],
                at: at(i),
                keys: []
            }));
    }
    const note = `${a.read.toLocaleString('en-US')} rows read${a.skipped ? `, ${a.skipped.toLocaleString('en-US')} without a number skipped` : ''}`;
    attrs.source = [
        str(node.attrs.source),
        note
    ].filter(Boolean).join(' · ');
    return {
        tag: 'Chart',
        attrs,
        children: kidsOut,
        at: node.at,
        keys: kidsOut.map((k)=>`n${k.at}`)
    };
}
function FileChart({ node, renderMarkdown }) {
    const host = React.useContext(HostContext);
    const file = str(node.attrs.file);
    const [state, setState] = React.useState({});
    React.useEffect(()=>{
        let live = true;
        if (!host.loadFile) {
            setState({
                error: 'Charts from files are not available here.'
            });
            return;
        }
        host.loadFile(file).then((text)=>{
            const agg = str(node.attrs.agg) || 'sum';
            const records = parseCsv(text);
            const headers = records.length ? Object.keys(records[0]) : [];
            const wanted = {
                x: str(node.attrs.x),
                y: agg === 'count' ? '' : str(node.attrs.y),
                series: str(node.attrs.series)
            };
            const cols = {};
            const missing = [];
            for (const [k, name] of Object.entries(wanted)){
                if (!name) continue;
                cols[k] = resolveColumn(headers, name);
                if (!cols[k]) missing.push(`"${name}"`);
            }
            if (!records.length || missing.length) {
                if (live) {
                    setState({
                        error: records.length ? `The file has no column ${missing.join(' or ')}. Its columns: ${headers.join(', ')}.` : 'The file has no rows.'
                    });
                }
                return;
            }
            const a = aggregate(records, {
                x: cols.x ?? '',
                y: cols.y,
                agg,
                series: cols.series,
                top: node.attrs.top !== undefined ? num(node.attrs.top) : str(node.attrs.type) === 'line' ? 0 : 25,
                sortByX: str(node.attrs.type) === 'line'
            });
            if (live) setState({
                chart: chartFromTable(node, a)
            });
        }).catch(()=>{
            if (live) setState({
                error: 'The file could not be read.'
            });
        });
        return ()=>{
            live = false;
        };
    }, [
        host,
        file,
        node
    ]);
    if (state.error) return /*#__PURE__*/ _jsx(FileCard, {
        title: str(node.attrs.title),
        detail: state.error
    });
    if (!state.chart) return /*#__PURE__*/ _jsx(Pending, {
        tag: "chart"
    });
    return /*#__PURE__*/ _jsx(Chart, {
        node: state.chart,
        renderMarkdown: renderMarkdown
    });
}
function Chart({ node, renderMarkdown }) {
    if (node.attrs.file !== undefined) return /*#__PURE__*/ _jsx(FileChart, {
        node: node,
        renderMarkdown: renderMarkdown
    });
    const unit = str(node.attrs.unit);
    const fmt = (v)=>formatUnit(v, unit);
    const title = str(node.attrs.title);
    const insight = str(node.attrs.insight) || undefined;
    const source = str(node.attrs.source);
    const type = str(node.attrs.type);
    if (type === 'line') {
        const points = kids(node, 'Point').map((p, i)=>({
                id: `pt-${i}`,
                label: str(p.attrs.label),
                value: num(p.attrs.value),
                title: str(p.attrs.label),
                detail: str(p.attrs.detail) || undefined
            }));
        const values = points.map((p)=>p.value).filter((v)=>Number.isFinite(v));
        const target = node.attrs.target !== undefined ? num(node.attrs.target) : undefined;
        const all = target !== undefined ? [
            ...values,
            target
        ] : values;
        const lo = Math.min(...all);
        const hi = Math.max(...all);
        const pad = (hi - lo) * 0.1 || Math.abs(hi) * 0.1 || 1;
        return /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx(SpecraTrendChart, {
                    animate: true,
                    title: title,
                    insight: insight,
                    points: points,
                    seriesLabel: title,
                    format: fmt,
                    min: lo >= 0 && lo - pad < 0 ? 0 : lo - pad,
                    // A rate that never passes 100% gets no axis above it.
                    max: unit === '%' && hi <= 100 ? Math.min(100, hi + pad) : hi + pad,
                    target: target !== undefined ? {
                        value: target,
                        label: str(node.attrs.targetLabel) || 'Target'
                    } : undefined,
                    emptyMessage: "No values to chart."
                }),
                /*#__PURE__*/ _jsx(Source, {
                    source: source
                })
            ]
        });
    }
    let segments;
    let rows;
    if (type === 'stacked') {
        segments = kids(node, 'Segment').map((s, i)=>({
                key: str(s.attrs.key),
                label: str(s.attrs.label),
                tone: str(s.attrs.tone) || SERIES_TONES[i % SERIES_TONES.length]
            }));
        rows = kids(node, 'Bar').map((b, i)=>{
            const values = Object.fromEntries(Object.entries(b.attrs.values ?? {}).map(([k, v])=>[
                    k,
                    num(v)
                ]));
            const total = Object.values(values).reduce((a, v)=>a + v, 0);
            return {
                id: `row-${i}`,
                label: str(b.attrs.label),
                sublabel: str(b.attrs.sublabel) || undefined,
                values,
                value: fmt(total)
            };
        });
    } else {
        segments = [
            {
                key: 'value',
                label: unit ? `Value (${unit})` : 'Value',
                tone: 'series'
            }
        ];
        rows = kids(node, 'Bar').map((b, i)=>({
                id: `row-${i}`,
                label: str(b.attrs.label),
                sublabel: str(b.attrs.sublabel) || undefined,
                values: {
                    value: num(b.attrs.value)
                },
                value: fmt(num(b.attrs.value))
            }));
    }
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(SpecraStackedBarList, {
                animate: true,
                title: title,
                insight: insight,
                segments: segments,
                rows: rows,
                valueLabel: unit || 'Value',
                emptyMessage: "No values to chart."
            }),
            /*#__PURE__*/ _jsx(Source, {
                source: source
            })
        ]
    });
}
function Timeline({ node }) {
    const items = kids(node, 'Event').map((e, i)=>({
            id: `ev-${i}`,
            title: str(e.attrs.title),
            description: str(e.attrs.description) || undefined,
            date: str(e.attrs.date),
            tag: str(e.attrs.tag) || undefined,
            tone: tone(e.attrs.tone)
        }));
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Heading, {
                title: str(node.attrs.title)
            }),
            /*#__PURE__*/ _jsx(SpecraTimeline, {
                items: items
            })
        ]
    });
}
/** A YYYY-MM or YYYY-MM-DD date as a day number (UTC). */ function day(v) {
    const s = str(v);
    const full = /^\d{4}-\d{2}$/.test(s) ? `${s}-01` : s;
    return Date.parse(`${full}T00:00:00Z`) / 86400000;
}
function Schedule({ node }) {
    const rowNodes = kids(node, 'Row');
    const markerNodes = kids(node, 'Marker');
    const points = [];
    for (const r of rowNodes)for (const s of kids(r, 'Span'))points.push(day(s.attrs.from), day(s.attrs.to));
    for (const m of markerNodes)points.push(day(m.attrs.at));
    if (node.attrs.today !== undefined) points.push(day(node.attrs.today));
    const lo = Math.min(...points);
    const hi = Math.max(...points);
    const span = hi - lo || 1;
    const pos = (d)=>Math.max(0, Math.min(100, (d - lo) / span * 100));
    const rows = rowNodes.map((r, i)=>({
            id: `row-${i}`,
            label: str(r.attrs.label),
            bars: kids(r, 'Span').map((s, j)=>{
                const start = pos(day(s.attrs.from));
                return {
                    id: `bar-${i}-${j}`,
                    label: str(s.attrs.label),
                    start,
                    width: Math.max(1, pos(day(s.attrs.to)) - start),
                    tone: tone(s.attrs.tone)
                };
            })
        }));
    const markers = markerNodes.map((m, i)=>({
            id: `mk-${i}`,
            position: pos(day(m.attrs.at)),
            label: str(m.attrs.label)
        }));
    if (node.attrs.today !== undefined) {
        markers.push({
            id: 'mk-today',
            position: pos(day(node.attrs.today)),
            label: 'Today'
        });
    }
    return /*#__PURE__*/ _jsx(SpecraGantt, {
        title: str(node.attrs.title),
        rows: rows,
        markers: markers
    });
}
function Cell({ value }) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const t = tone(value.tone);
        const label = str(value.text);
        return t ? /*#__PURE__*/ _jsx(SpecraStatusPill, {
            tone: t,
            children: label
        }) : label;
    }
    return str(value);
}
/** A row's cells keyed by column, so React keys never depend on position. */ function cellsOf(tr, columns) {
    const cells = Array.isArray(tr.attrs.cells) ? tr.attrs.cells : [];
    return cells.map((value, n)=>({
            key: `${tr.at}:${columns[n] ?? n}`,
            value
        }));
}
/** A cell's text, for sorting and filtering. */ const cellText = (value)=>value && typeof value === 'object' && !Array.isArray(value) ? str(value.text) : str(value);
/** Rows shown before "Show all"; a filter box appears past FILTER_AT rows. */ const TABLE_PAGE = 15;
const FILTER_AT = 8;
function TableVisual({ node }) {
    const columns = (Array.isArray(node.attrs.columns) ? node.attrs.columns : []).map(str);
    const rows = kids(node, 'Tr');
    const [sort, setSort] = React.useState(null);
    const [query, setQuery] = React.useState('');
    const [all, setAll] = React.useState(false);
    const shown = React.useMemo(()=>{
        const cells = (tr)=>Array.isArray(tr.attrs.cells) ? tr.attrs.cells : [];
        const q = query.trim().toLowerCase();
        let out = q ? rows.filter((tr)=>cells(tr).some((c)=>cellText(c).toLowerCase().includes(q))) : rows;
        if (sort) {
            const key = (tr)=>cellText(cells(tr)[sort.col] ?? '');
            out = [
                ...out
            ].sort((a, b)=>{
                const x = key(a);
                const y = key(b);
                const nx = cellNumber(x);
                const ny = cellNumber(y);
                const byNumber = Number.isFinite(nx) && Number.isFinite(ny);
                return sort.dir * (byNumber ? nx - ny : x.localeCompare(y, undefined, {
                    numeric: true
                }));
            });
        }
        return out;
    }, [
        rows,
        sort,
        query
    ]);
    const visible = all ? shown : shown.slice(0, TABLE_PAGE);
    const toggle = (col)=>setSort((cur)=>cur?.col === col ? cur.dir === 1 ? {
                col,
                dir: -1
            } : null : {
                col,
                dir: 1
            });
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Heading, {
                title: str(node.attrs.title)
            }),
            rows.length > FILTER_AT && /*#__PURE__*/ _jsx("input", {
                type: "search",
                value: query,
                onChange: (e)=>setQuery(e.target.value),
                placeholder: `Filter ${rows.length} rows`,
                "aria-label": "Filter rows",
                "data-print-hide": true,
                className: "mb-2 w-full max-w-xs rounded-md border border-border bg-background px-2 py-1 text-[length:var(--fs-ui-caption)] text-foreground"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "overflow-x-auto rounded-md border border-border",
                children: /*#__PURE__*/ _jsxs(Table, {
                    children: [
                        /*#__PURE__*/ _jsx(TableHeader, {
                            children: /*#__PURE__*/ _jsx(TableRow, {
                                children: columns.map((c, i)=>/*#__PURE__*/ _jsx(TableHead, {
                                        "aria-sort": sort?.col === i ? sort.dir === 1 ? 'ascending' : 'descending' : undefined,
                                        children: /*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            onClick: ()=>toggle(i),
                                            className: "inline-flex items-center gap-1 font-medium hover:text-foreground",
                                            children: [
                                                c,
                                                /*#__PURE__*/ _jsx("span", {
                                                    "aria-hidden": true,
                                                    className: "text-muted-foreground",
                                                    children: sort?.col === i ? sort.dir === 1 ? '▲' : '▼' : ''
                                                })
                                            ]
                                        })
                                    }, c))
                            })
                        }),
                        /*#__PURE__*/ _jsx(TableBody, {
                            children: visible.map((tr)=>/*#__PURE__*/ _jsx(TableRow, {
                                    children: cellsOf(tr, columns).map((cell)=>/*#__PURE__*/ _jsx(TableCell, {
                                            children: /*#__PURE__*/ _jsx(Cell, {
                                                value: cell.value
                                            })
                                        }, cell.key))
                                }, tr.at))
                        })
                    ]
                })
            }),
            shown.length > TABLE_PAGE && !all && /*#__PURE__*/ _jsxs(SpecraButton, {
                size: "sm",
                variant: "ghost",
                className: "mt-1",
                onClick: ()=>setAll(true),
                children: [
                    "Show all ",
                    shown.length,
                    " rows"
                ]
            }),
            query && shown.length === 0 && /*#__PURE__*/ _jsx("p", {
                className: "mt-1 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: "No rows match."
            }),
            /*#__PURE__*/ _jsx(Source, {
                source: str(node.attrs.source)
            })
        ]
    });
}
function Facts({ node }) {
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Heading, {
                title: str(node.attrs.title)
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "space-y-1",
                children: kids(node, 'Fact').map((f)=>/*#__PURE__*/ _jsx(SpecraKeyValueRow, {
                        label: str(f.attrs.label),
                        value: str(f.attrs.value),
                        variant: f.attrs.numeric ? 'numeric' : 'default'
                    }, f.at))
            })
        ]
    });
}
function Callout({ node, renderMarkdown }) {
    return /*#__PURE__*/ _jsx(SpecraInlineBanner, {
        variant: "framed",
        tone: tone(node.attrs.tone) ?? 'info',
        title: str(node.attrs.title) || undefined,
        children: renderMarkdown(text(node))
    });
}
function Steps({ node, renderMarkdown }) {
    const items = kids(node, 'Step').map((s, i)=>({
            id: `step-${i}`,
            title: str(s.attrs.title),
            date: `${i + 1}`,
            description: text(s) ? renderMarkdown(text(s)) : undefined,
            tag: str(s.attrs.tag) || undefined,
            tone: tone(s.attrs.tone)
        }));
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Heading, {
                title: str(node.attrs.title)
            }),
            /*#__PURE__*/ _jsx(SpecraTimeline, {
                items: items
            })
        ]
    });
}
/** A node's children with the stable keys the parser gave them. */ function keyed(node) {
    return node.children.map((child, n)=>({
            key: node.keys[n] ?? `${node.at}.${n}`,
            child
        }));
}
function Children({ node, renderMarkdown }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-3",
        children: keyed(node).map(({ key, child })=>typeof child === 'string' ? /*#__PURE__*/ _jsx(React.Fragment, {
                children: renderMarkdown(child)
            }, key) : /*#__PURE__*/ _jsx(SpecraVisual, {
                node: child,
                renderMarkdown: renderMarkdown
            }, key))
    });
}
function Tabs({ node, renderMarkdown }) {
    const tabs = kids(node, 'Tab');
    const idBase = React.useId();
    const options = tabs.map((t)=>({
            value: `tab-${t.at}`,
            label: str(t.attrs.label)
        }));
    const [value, setValue] = React.useState(options[0]?.value ?? '');
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(SpecraTabBar, {
                options: options,
                value: value,
                onValueChange: setValue,
                idBase: idBase
            }),
            tabs.map((t)=>/*#__PURE__*/ _jsx(SpecraTabPanel, {
                    idBase: idBase,
                    value: `tab-${t.at}`,
                    active: value === `tab-${t.at}`,
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "pt-3",
                        children: /*#__PURE__*/ _jsx(Children, {
                            node: t,
                            renderMarkdown: renderMarkdown
                        })
                    })
                }, t.at))
        ]
    });
}
function Cards({ node, renderMarkdown }) {
    const cols = Math.min(4, Math.max(1, num(node.attrs.columns) || 2));
    return /*#__PURE__*/ _jsx("div", {
        className: "grid gap-3",
        style: {
            gridTemplateColumns: `repeat(auto-fit, minmax(${cols >= 3 ? 180 : 240}px, 1fr))`
        },
        children: kids(node, 'Card').map((c)=>/*#__PURE__*/ _jsxs(SpecraCard, {
                children: [
                    /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraCardTitle, {
                                className: "flex items-center gap-2",
                                children: [
                                    tone(c.attrs.tone) && /*#__PURE__*/ _jsx(SpecraStatusDot, {
                                        tone: tone(c.attrs.tone)
                                    }),
                                    str(c.attrs.href) ? /*#__PURE__*/ _jsx("a", {
                                        href: str(c.attrs.href),
                                        className: "hover:underline",
                                        children: str(c.attrs.title)
                                    }) : str(c.attrs.title)
                                ]
                            }),
                            str(c.attrs.subtitle) && /*#__PURE__*/ _jsx("div", {
                                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                children: str(c.attrs.subtitle)
                            })
                        ]
                    }),
                    text(c) && /*#__PURE__*/ _jsx(SpecraCardContent, {
                        children: renderMarkdown(text(c))
                    })
                ]
            }, c.at))
    });
}
function FileCard({ title, detail, quote }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-md border border-border bg-card p-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 text-[length:var(--fs-ui-caption)] font-medium text-foreground",
                children: [
                    /*#__PURE__*/ _jsx(FileText, {
                        className: "h-4 w-4 text-muted-foreground",
                        "aria-hidden": true
                    }),
                    title
                ]
            }),
            detail && /*#__PURE__*/ _jsx("div", {
                className: "mt-0.5 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: detail
            }),
            quote && /*#__PURE__*/ _jsx("blockquote", {
                className: "mt-2 border-l-2 border-border pl-2 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: quote
            })
        ]
    });
}
/**
 * Code the assistant wrote, run in a sandbox with no network. It is labelled
 * an illustration: figures the user relies on live in the catalog visuals.
 * Without the host's go-ahead (the flag is off) it shows as its description.
 */ function Custom({ node }) {
    const host = React.useContext(HostContext);
    const title = str(node.attrs.title);
    const description = str(node.attrs.description);
    if (!host.customVisuals) return /*#__PURE__*/ _jsx(FileCard, {
        title: title,
        detail: description
    });
    const height = node.attrs.height !== undefined ? num(node.attrs.height) : 360;
    return /*#__PURE__*/ _jsxs("figure", {
        className: "m-0",
        children: [
            /*#__PURE__*/ _jsx(Heading, {
                title: title
            }),
            /*#__PURE__*/ _jsx(CustomFrame, {
                code: text(node),
                title: title,
                description: description,
                height: Math.min(720, Math.max(160, height || 360))
            }),
            /*#__PURE__*/ _jsxs("figcaption", {
                className: "mt-1 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "Illustration · ",
                    description
                ]
            })
        ]
    });
}
function docPageProps(node) {
    return {
        file: str(node.attrs.file),
        page: node.attrs.page !== undefined ? num(node.attrs.page) : undefined,
        quote: str(node.attrs.quote) || undefined,
        caption: str(node.attrs.caption) || undefined
    };
}
function DocPage({ node }) {
    const host = React.useContext(HostContext);
    const props = docPageProps(node);
    if (host.DocPage) return /*#__PURE__*/ _jsx(_Fragment, {
        children: host.DocPage(props)
    });
    return /*#__PURE__*/ _jsx(FileCard, {
        title: props.caption ?? 'Source page',
        detail: props.page ? `Page ${props.page}` : undefined,
        quote: props.quote
    });
}
function ImageVisual({ node }) {
    const host = React.useContext(HostContext);
    const props = {
        file: str(node.attrs.file),
        alt: str(node.attrs.alt),
        caption: str(node.attrs.caption) || undefined
    };
    if (host.Image) return /*#__PURE__*/ _jsx(_Fragment, {
        children: host.Image(props)
    });
    return /*#__PURE__*/ _jsx(FileCard, {
        title: props.caption ?? props.alt
    });
}
function Carousel({ node, renderMarkdown }) {
    const slides = node.children.filter((c)=>typeof c !== 'string').map((c)=>{
        const label = str(c.attrs.caption) || str(c.attrs.label) || str(c.attrs.alt) || undefined;
        const content = c.tag === 'DocPage' ? /*#__PURE__*/ _jsx(DocPage, {
            node: c,
            renderMarkdown: renderMarkdown
        }, c.at) : c.tag === 'Image' ? /*#__PURE__*/ _jsx(ImageVisual, {
            node: c,
            renderMarkdown: renderMarkdown
        }, c.at) : /*#__PURE__*/ _jsx(Children, {
            node: c,
            renderMarkdown: renderMarkdown
        }, c.at);
        return {
            id: String(c.at),
            label,
            content
        };
    });
    const title = str(node.attrs.title);
    return /*#__PURE__*/ _jsx(SpecraCarousel, {
        slides: slides,
        label: title || 'Slides',
        title: title || undefined
    });
}
function Flow({ node }) {
    const nodes = kids(node, 'Node').map((n)=>({
            id: str(n.attrs.id),
            label: str(n.attrs.label),
            detail: str(n.attrs.detail) || undefined,
            tone: tone(n.attrs.tone),
            step: n.attrs.step !== undefined ? num(n.attrs.step) : undefined
        }));
    const edges = kids(node, 'Edge').map((e)=>({
            from: str(e.attrs.from),
            to: str(e.attrs.to),
            label: str(e.attrs.label) || undefined,
            dashed: e.attrs.dashed === true,
            step: e.attrs.step !== undefined ? num(e.attrs.step) : undefined
        }));
    const title = str(node.attrs.title);
    return /*#__PURE__*/ _jsx(SpecraFlowDiagram, {
        nodes: nodes,
        edges: edges,
        direction: node.attrs.direction === 'LR' ? 'LR' : 'TB',
        title: title || undefined,
        label: title || 'Diagram',
        walkthrough: node.attrs.walkthrough === true,
        animated: node.attrs.animated === true
    });
}
/** The answer up top: one big figure, what it means, a few supporting stats. */ function Hero({ node }) {
    const t = tone(node.attrs.tone);
    const stats = kids(node, 'Stat');
    return /*#__PURE__*/ _jsxs("section", {
        className: "specra-visual-enter rounded-md border border-border bg-card p-[var(--space-4)]",
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                as: "p",
                size: "caption",
                tone: "muted",
                children: str(node.attrs.label)
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "font-display text-[length:var(--fs-ui-verdict)] font-medium leading-tight tabular-nums text-foreground",
                children: /*#__PURE__*/ _jsx(SpecraCountUp, {
                    value: str(node.attrs.value)
                })
            }),
            node.attrs.verdict !== undefined && /*#__PURE__*/ _jsxs("p", {
                className: "mt-[var(--space-1)] flex items-center gap-[var(--space-2)] text-[length:var(--fs-ui)] text-foreground",
                children: [
                    t && /*#__PURE__*/ _jsx(SpecraStatusDot, {
                        tone: t
                    }),
                    str(node.attrs.verdict)
                ]
            }),
            stats.length > 0 && /*#__PURE__*/ _jsx("dl", {
                className: "mt-[var(--space-3)] grid grid-cols-1 gap-[var(--space-3)] border-t border-border pt-[var(--space-3)] sm:grid-cols-3",
                children: stats.map((st)=>/*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("dt", {
                                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                children: str(st.attrs.label)
                            }),
                            /*#__PURE__*/ _jsx("dd", {
                                className: "m-0 text-[length:var(--fs-ui-title)] font-medium tabular-nums text-foreground",
                                children: /*#__PURE__*/ _jsx(SpecraCountUp, {
                                    value: str(st.attrs.value)
                                })
                            }),
                            st.attrs.note !== undefined && /*#__PURE__*/ _jsx("dd", {
                                className: "m-0 text-[length:var(--fs-ui-caption)] text-muted-foreground",
                                children: str(st.attrs.note)
                            })
                        ]
                    }, st.at))
            }),
            /*#__PURE__*/ _jsx(Source, {
                source: str(node.attrs.source)
            })
        ]
    });
}
/** Two or three visuals side by side; they stack when the column is narrow. */ function Columns({ node, renderMarkdown }) {
    const parts = node.children.filter((x)=>typeof x !== 'string');
    return /*#__PURE__*/ _jsx("div", {
        className: "grid grid-cols-[repeat(auto-fit,minmax(min(100%,var(--component-visual-column-min)),1fr))] items-start gap-[var(--space-3)] [&>*]:my-0",
        children: parts.map((part)=>/*#__PURE__*/ _jsx(SpecraVisual, {
                node: part,
                renderMarkdown: renderMarkdown
            }, part.at))
    });
}
/** A catalog tone as a chart colour. */ const CHART_TONE = {
    success: 'good',
    warning: 'warn',
    danger: 'bad',
    neutral: 'pending',
    info: 'series'
};
const chartTone = (v)=>v === undefined ? undefined : CHART_TONE[str(v)];
/** A stable key for a viewer's ticks on one checklist. */ function checklistKey(node) {
    const text = [
        str(node.attrs.title),
        ...kids(node, 'Item').map((i)=>str(i.attrs.label))
    ].join('|');
    let h = 5381;
    for(let i = 0; i < text.length; i++)h = (h << 5) + h + text.charCodeAt(i) | 0;
    return `specra-checklist:${(h >>> 0).toString(36)}`;
}
function Checklist({ node }) {
    const items = kids(node, 'Item').map((i)=>({
            id: `item-${i.at}`,
            label: str(i.attrs.label),
            detail: str(i.attrs.detail) || undefined,
            done: i.attrs.done === true,
            due: str(i.attrs.due) || undefined,
            owner: str(i.attrs.owner) || undefined,
            tone: str(i.attrs.tone) || undefined
        }));
    return /*#__PURE__*/ _jsx(SpecraChecklist, {
        title: str(node.attrs.title) || undefined,
        items: items,
        storageKey: checklistKey(node),
        source: str(node.attrs.source) || undefined
    });
}
function MapVisual({ node }) {
    const unit = str(node.attrs.unit);
    return /*#__PURE__*/ _jsx(SpecraTileMap, {
        title: str(node.attrs.title),
        insight: str(node.attrs.insight) || undefined,
        source: str(node.attrs.source) || undefined,
        valueLabel: str(node.attrs.valueLabel) || undefined,
        format: (v)=>formatUnit(v, unit),
        states: kids(node, 'State').map((st)=>({
                code: str(st.attrs.code).toUpperCase(),
                value: st.attrs.value !== undefined ? num(st.attrs.value) : undefined,
                tone: chartTone(st.attrs.tone),
                note: str(st.attrs.note) || undefined
            }))
    });
}
function Treemap({ node }) {
    const unit = str(node.attrs.unit);
    return /*#__PURE__*/ _jsx(SpecraTreemap, {
        title: str(node.attrs.title),
        insight: str(node.attrs.insight) || undefined,
        source: str(node.attrs.source) || undefined,
        format: (v)=>formatUnit(v, unit),
        items: kids(node, 'Tile').map((t)=>({
                id: `tile-${t.at}`,
                label: str(t.attrs.label),
                value: num(t.attrs.value),
                group: str(t.attrs.group) || undefined,
                tone: chartTone(t.attrs.tone)
            }))
    });
}
function Sankey({ node }) {
    const unit = str(node.attrs.unit);
    return /*#__PURE__*/ _jsx(SpecraSankey, {
        title: str(node.attrs.title),
        insight: str(node.attrs.insight) || undefined,
        source: str(node.attrs.source) || undefined,
        format: (v)=>formatUnit(v, unit),
        links: kids(node, 'Link').map((l)=>({
                from: str(l.attrs.from),
                to: str(l.attrs.to),
                value: num(l.attrs.value),
                tone: chartTone(l.attrs.tone)
            }))
    });
}
function scenarioInputs(node) {
    const opt = (v)=>v === undefined ? undefined : num(v);
    return kids(node, 'Input').map((i)=>({
            id: str(i.attrs.id),
            label: str(i.attrs.label),
            value: num(i.attrs.value),
            min: opt(i.attrs.min),
            max: opt(i.attrs.max),
            step: opt(i.attrs.step),
            unit: str(i.attrs.unit) || undefined
        }));
}
/** The reply's levers: moving one moves every value bound to it. */ function Inputs({ node }) {
    const shared = React.useContext(ReplyInputsContext);
    return /*#__PURE__*/ _jsx(SpecraScenario, {
        title: str(node.attrs.title) || 'Adjust the assumptions',
        description: str(node.attrs.description) || 'The figures in this reply follow these values.',
        inputs: scenarioInputs(node),
        outputs: [],
        values: shared?.values,
        onValuesChange: shared?.set
    });
}
function Scenario({ node }) {
    const shared = React.useContext(ReplyInputsContext);
    const opt = (v)=>v === undefined ? undefined : num(v);
    return /*#__PURE__*/ _jsx(SpecraScenario, {
        values: shared?.values,
        onValuesChange: shared?.set,
        title: str(node.attrs.title) || undefined,
        description: str(node.attrs.description) || undefined,
        source: str(node.attrs.source) || undefined,
        inputs: kids(node, 'Input').map((i)=>({
                id: str(i.attrs.id),
                label: str(i.attrs.label),
                value: num(i.attrs.value),
                min: opt(i.attrs.min),
                max: opt(i.attrs.max),
                step: opt(i.attrs.step),
                unit: str(i.attrs.unit) || undefined
            })),
        outputs: kids(node, 'Output').map((o)=>({
                label: str(o.attrs.label),
                formula: str(o.attrs.formula),
                unit: str(o.attrs.unit) || undefined,
                primary: o.attrs.primary === true,
                note: str(o.attrs.note) || undefined
            }))
    });
}
function OrgTree({ node }) {
    const members = kids(node, 'Member');
    const nodes = members.map((m)=>({
            id: str(m.attrs.id),
            label: str(m.attrs.label),
            detail: str(m.attrs.detail) || undefined,
            tone: tone(m.attrs.tone)
        }));
    const edges = members.filter((m)=>m.attrs.parent !== undefined).map((m)=>({
            from: str(m.attrs.parent),
            to: str(m.attrs.id)
        }));
    const title = str(node.attrs.title);
    return /*#__PURE__*/ _jsx(SpecraFlowDiagram, {
        nodes: nodes,
        edges: edges,
        direction: "TB",
        title: title || undefined,
        label: title || 'Org chart'
    });
}
function Actions({ node }) {
    const host = React.useContext(HostContext);
    const actions = kids(node, 'Action').map((a)=>({
            at: a.at,
            primary: a.attrs.primary === true,
            action: {
                kind: str(a.attrs.kind),
                label: str(a.attrs.label),
                prompt: str(a.attrs.prompt) || undefined,
                href: str(a.attrs.href) || undefined,
                file: str(a.attrs.file) || undefined
            }
        }));
    return /*#__PURE__*/ _jsx("div", {
        className: "flex flex-wrap gap-2",
        children: actions.map(({ at, primary, action })=>{
            const runnable = Boolean(host.onAction) && (host.canAct ? host.canAct(action) : true);
            return /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: primary ? 'default' : 'outline',
                disabled: !runnable,
                onClick: ()=>host.onAction?.(action),
                children: action.label
            }, at);
        })
    });
}
const LEVEL = {
    low: 1,
    medium: 2,
    high: 3
};
function Risks({ node }) {
    const cells = kids(node, 'Risk').map((r)=>{
        const impact = str(r.attrs.impact);
        const probability = str(r.attrs.probability);
        const score = LEVEL[impact] * LEVEL[probability];
        const t = score >= 9 ? 'danger' : score >= 6 ? 'orange' : score >= 3 ? 'warning' : 'neutral';
        return {
            impact,
            probability,
            count: num(r.attrs.count),
            tone: t
        };
    });
    return /*#__PURE__*/ _jsx(SpecraRiskMatrix, {
        title: str(node.attrs.title) || undefined,
        cells: cells
    });
}
const RENDERERS = {
    Stats,
    Chart,
    Timeline,
    Schedule,
    Table: TableVisual,
    Facts,
    Callout,
    Steps,
    Tabs,
    Cards,
    Risks,
    DocPage,
    Image: ImageVisual,
    Carousel,
    Flow,
    OrgTree,
    Actions,
    Hero,
    Columns,
    Inputs,
    Checklist,
    Map: MapVisual,
    Treemap,
    Sankey,
    Scenario,
    Custom
};
/** Visuals that get room to breathe: an expand button opens them full size. */ const EXPANDABLE = new Set([
    'Chart',
    'Schedule',
    'Timeline',
    'Table',
    'Flow',
    'OrgTree',
    'Map',
    'Treemap',
    'Sankey',
    'Scenario',
    'Risks'
]);
/** One catalog visual, drawn with Specra components. */ export function SpecraVisual({ node: raw, renderMarkdown }) {
    const [expanded, setExpanded] = React.useState(false);
    const inputs = React.useContext(ReplyInputsContext);
    const values = inputs?.values;
    const node = React.useMemo(()=>values && hasFormula(raw) ? resolveNode(raw, values) : raw, [
        raw,
        values
    ]);
    const Render = RENDERERS[node.tag];
    if (!Render) return null;
    const title = str(node.attrs.title) || node.tag;
    return /*#__PURE__*/ _jsxs("div", {
        className: "specra-visual-enter group/visual relative my-3",
        "data-visual": node.tag,
        children: [
            EXPANDABLE.has(node.tag) && /*#__PURE__*/ _jsx(SpecraIconButton, {
                label: `Expand ${title}`,
                size: "sm",
                variant: "ghost",
                "data-print-hide": true,
                onClick: ()=>setExpanded(true),
                className: "absolute right-0 top-0 z-10 text-muted-foreground opacity-0 transition-opacity focus-visible:opacity-100 group-hover/visual:opacity-100",
                children: /*#__PURE__*/ _jsx(Maximize2, {
                    "aria-hidden": "true"
                })
            }),
            /*#__PURE__*/ _jsx(Render, {
                node: node,
                renderMarkdown: renderMarkdown
            }),
            expanded && /*#__PURE__*/ _jsx(SpecraOverlayDialog, {
                open: true,
                onOpenChange: setExpanded,
                title: title,
                size: "xl",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "p-4",
                    children: /*#__PURE__*/ _jsx(Render, {
                        node: node,
                        renderMarkdown: renderMarkdown
                    })
                })
            })
        ]
    });
}
function Pending({ tag }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "my-3 flex items-center gap-2 rounded-md border border-dashed border-border p-3 text-[length:var(--fs-ui-caption)] text-muted-foreground",
        children: [
            /*#__PURE__*/ _jsx(SpecraSpinner, {
                size: "sm"
            }),
            "Drawing ",
            tag.toLowerCase(),
            "…"
        ]
    });
}
/**
 * A reply with visuals: markdown runs go to the host renderer, catalog tags
 * are drawn with Specra components, a tag still streaming shows a placeholder,
 * and a tag that fails validation shows as its raw text in a code block.
 */ export function SpecraVisualMarkdown({ content, renderMarkdown, onInvalid, host }) {
    const segments = React.useMemo(()=>splitVisuals(content), [
        content
    ]);
    React.useEffect(()=>{
        for (const s of segments)if (s.kind === 'invalid') onInvalid?.(s);
    }, [
        segments,
        onInvalid
    ]);
    const hostValue = React.useMemo(()=>host ?? {}, [
        host
    ]);
    // The reply's inputs start at the values it states; the viewer's moves
    // override them for this view only (the reply itself never changes).
    const initialInputs = React.useMemo(()=>declaredInputs(segments), [
        segments
    ]);
    const [moved, setMoved] = React.useState({});
    const inputs = React.useMemo(()=>({
            values: {
                ...initialInputs,
                ...moved
            },
            set: setMoved
        }), [
        initialInputs,
        moved
    ]);
    return /*#__PURE__*/ _jsx(HostContext.Provider, {
        value: hostValue,
        children: /*#__PURE__*/ _jsx(ReplyInputsContext.Provider, {
            value: inputs,
            children: segments.map((s)=>{
                switch(s.kind){
                    case 'markdown':
                        return /*#__PURE__*/ _jsx(React.Fragment, {
                            children: renderMarkdown(s.text)
                        }, s.at);
                    case 'visual':
                        return /*#__PURE__*/ _jsx(SpecraVisual, {
                            node: s.node,
                            renderMarkdown: renderMarkdown
                        }, s.at);
                    case 'pending':
                        return /*#__PURE__*/ _jsx(Pending, {
                            tag: s.tag
                        }, s.at);
                    default:
                        return /*#__PURE__*/ _jsx(React.Fragment, {
                            children: renderMarkdown(`\`\`\`\n${s.raw}\n\`\`\``)
                        }, s.at);
                }
            })
        })
    });
}
const VISUAL_START = new RegExp(`(^|\\n)\\s*<(${visualCatalog.topLevel.join('|')})[\\s/>]`);
/** True when the text holds a catalog tag (cheap check before splitting). */ export function hasVisuals(content) {
    return VISUAL_START.test(content);
}
